import React from 'react';
import { useSync, SYNC_STATUS } from '../contexts/SyncContext';
import { Cloud, CloudOff, Check, Loader2, AlertCircle, Wifi, WifiOff, CloudUpload, CheckCircle } from 'lucide-react';

/**
 * SyncIndicator - Shows cloud sync status
 * 
 * Usage: Place in header or floating position
 * <SyncIndicator />
 * <SyncIndicator showLabel={true} />
 * <SyncIndicator compact={true} />
 */
export function SyncIndicator({ showLabel = false, compact = false, className = '' }) {
  const { status, lastSynced, lastBackup, pendingCount, errorMessage, isOnline } = useSync();

  // Icon size: compact=16px, normal=20px (matching timer icon size)
  const iconSize = compact ? 'h-4 w-4' : 'h-5 w-5';

  const getStatusConfig = () => {
    switch (status) {
      case SYNC_STATUS.BACKING_UP:
        return {
          icon: <CloudUpload className={`${iconSize} animate-pulse`} />,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
          label: 'Backing up...',
          pulse: true
        };
      case SYNC_STATUS.BACKED_UP:
        return {
          icon: <CheckCircle className={iconSize} />,
          color: 'text-green-400',
          bgColor: 'bg-green-500/20',
          label: 'Backup complete',
          pulse: false
        };
      case SYNC_STATUS.SYNCING:
        return {
          icon: <Loader2 className={`${iconSize} animate-spin`} />,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
          label: pendingCount > 1 ? `Syncing (${pendingCount})...` : 'Syncing...',
          pulse: true
        };
      case SYNC_STATUS.SYNCED:
        return {
          icon: <Check className={iconSize} />,
          color: 'text-green-400',
          bgColor: 'bg-green-500/20',
          label: 'Synced',
          pulse: false
        };
      case SYNC_STATUS.OFFLINE:
        return {
          icon: <WifiOff className={iconSize} />,
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/20',
          label: 'Offline',
          pulse: false
        };
      case SYNC_STATUS.ERROR:
        return {
          icon: <AlertCircle className={iconSize} />,
          color: 'text-red-400',
          bgColor: 'bg-red-500/20',
          label: 'Sync failed',
          pulse: false
        };
      default: // IDLE
        return {
          icon: <Cloud className={iconSize} />,
          color: isOnline ? 'text-slate-400' : 'text-yellow-400',
          bgColor: 'bg-slate-500/10',
          label: isOnline ? 'Cloud ready' : 'Offline',
          pulse: false
        };
    }
  };

  const config = getStatusConfig();

  // Format last synced/backup time
  const formatLastSynced = (date = lastSynced) => {
    if (!date) return null;
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  if (compact) {
    return (
      <div 
        className={`inline-flex items-center ${config.color} ${className}`}
        title={errorMessage || config.label}
      >
        {config.icon}
      </div>
    );
  }

  return (
    <div 
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bgColor} ${config.color} transition-all duration-300 ${className}`}
      title={errorMessage || (lastBackup ? `Last backup: ${formatLastSynced(lastBackup)}` : (lastSynced ? `Last synced: ${formatLastSynced()}` : ''))}
    >
      <div className={config.pulse ? 'animate-pulse' : ''}>
        {config.icon}
      </div>
      {showLabel && (
        <span className="text-xs font-medium">
          {config.label}
        </span>
      )}
    </div>
  );
}

/**
 * FloatingSyncIndicator - Fixed position indicator
 * Shows in bottom-right corner when syncing or on error
 */
export function FloatingSyncIndicator() {
  const { status } = useSync();
  
  // Only show when actively syncing or on error/offline
  const shouldShow = [
    SYNC_STATUS.BACKING_UP,
    SYNC_STATUS.BACKED_UP,
    SYNC_STATUS.SYNCING,
    SYNC_STATUS.ERROR,
    SYNC_STATUS.OFFLINE,
    SYNC_STATUS.SYNCED
  ].includes(status);

  if (!shouldShow) return null;

  return (
    <div className="fixed bottom-20 right-4 z-40 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <SyncIndicator showLabel={true} />
    </div>
  );
}

export default SyncIndicator;
